import { Routes, Route, Navigate } from 'react-router';
import { Suspense, useEffect } from 'react';
import { lazyPage } from '@skupreme/shared/deploy';
import { LoaderBar } from '@components/LoaderBar';
import App from './App';

// Lazy load all pages for code splitting. lazyPage, not lazy: a chunk the
// last deploy removed shows a Reload prompt instead of reloading the tab.
// Root level pages
const NotFound = lazyPage(() => import('./pages/404'));
const AcceptInvite = lazyPage(() => import('./pages/accept-invite'));
const CmsLogin = lazyPage(() => import('./pages/cms-login'));
const ForgotPassword = lazyPage(() => import('./pages/forgot-password'));
const Login = lazyPage(() => import('./pages/login'));
const Redirect = lazyPage(() => import('./pages/redirect'));
const RequestPassword = lazyPage(() => import('./pages/request-password'));
const ShopifyRedirect = lazyPage(() => import('./pages/shopify-redirect'));
const Sso = lazyPage(() => import('./pages/sso'));
const Handoff = lazyPage(() => import('./pages/handoff'));
const Tactical = lazyPage(() => import('./pages/tactical'));
const Usps = lazyPage(() => import('./pages/usps'));

// Deliverr

// Shopify
const ShopifyIndex = lazyPage(() => import('./pages/shopify/index'));
const ShopifyBilling = lazyPage(() => import('./pages/shopify/billing'));

// Public pages
const PublicOrder = lazyPage(() => import('./pages/public/order'));
const PublicSupplierPurchaseOrders = lazyPage(
  () => import('./pages/public/supplier/purchase-orders'),
);
const PublicWholesalerInvoices = lazyPage(
  () => import('./pages/public/wholesaler/wholesaler-invoices'),
);

// WMS pages

// App pages
const AppIndex = lazyPage(() => import('./pages/app/index'));
const AppChangeLogs = lazyPage(() => import('./pages/app/change-logs'));
const AppNotifications = lazyPage(() => import('./pages/app/notifications'));
const AppOnboarding = lazyPage(() => import('./pages/app/onboarding'));
const AppSettings = lazyPage(() => import('./pages/app/settings'));
const BillingInvoices = lazyPage(() => import('./pages/app/billing/invoices'));

// App - Case Log

// App - Catalogs
const CatalogsIndex = lazyPage(() => import('./pages/app/catalogs/index'));
const CatalogsAttributes = lazyPage(
  () => import('./pages/app/catalogs/attributes'),
);
const CatalogsClassificationGuide = lazyPage(
  () => import('./pages/app/catalogs/classification-guide'),
);
const CatalogsDuplicateListings = lazyPage(
  () => import('./pages/app/catalogs/duplicate-listings'),
);
const CatalogsResolveConflict = lazyPage(
  () => import('./pages/app/catalogs/resolve-conflict'),
);
const CatalogIndex = lazyPage(
  () => import('./pages/app/catalogs/catalog/index'),
);
const CatalogDetails = lazyPage(
  () => import('./pages/app/catalogs/catalog/[id]'),
);
const CatalogsComponentsIndex = lazyPage(
  () => import('./pages/app/catalogs/components/index'),
);
const CatalogsComponentDetails = lazyPage(
  () => import('./pages/app/catalogs/components/[id]'),
);
const CatalogsListingsMatched = lazyPage(
  () => import('./pages/app/catalogs/listings/matched-items'),
);
const CatalogsListingsUnmatched = lazyPage(
  () => import('./pages/app/catalogs/listings/unmatched-items'),
);
const CatalogsSettingsTiktok = lazyPage(
  () => import('./pages/app/catalogs/settings/tiktok'),
);
const CatalogsMapping = lazyPage(
  () => import('./pages/app/catalogs/mapping/index'),
);

// App - CRM
const CrmIndex = lazyPage(() => import('./pages/app/crm/index'));
const CrmDetails = lazyPage(() => import('./pages/app/crm/[id]'));

// App - Dashboard
const DashboardAwaitingActions = lazyPage(
  () => import('./pages/app/dashboard/awaiting-actions'),
);
const DashboardLogs = lazyPage(() => import('./pages/app/dashboard/logs'));

// App - Data Studio
const DataStudioAnalytics = lazyPage(
  () => import('./pages/app/data-studio/analytics'),
);
const DataStudioFbaMonitoringChart = lazyPage(
  () => import('./pages/app/data-studio/fba-monitoring-chart'),
);
const DataStudioInventoryReport = lazyPage(
  () => import('./pages/app/data-studio/inventory-report'),
);
const DataStudioLostRevenue = lazyPage(
  () => import('./pages/app/data-studio/lost-revenue'),
);
const DataStudioManifestReports = lazyPage(
  () => import('./pages/app/data-studio/manifest-reports'),
);
const DataStudioTimeSeries = lazyPage(
  () => import('./pages/app/data-studio/time-series'),
);

// App - Orders
const OrderDetails = lazyPage(() => import('./pages/app/orders/[id]'));
const OrderClone = lazyPage(() => import('./pages/app/orders/clone/[id]'));
const OrderListByStatus = lazyPage(
  () => import('./pages/app/orders/list/[status]'),
);
const OrderListReturns = lazyPage(
  () => import('./pages/app/orders/list/Returns'),
);
const OrderListShipped = lazyPage(
  () => import('./pages/app/orders/list/Shipped'),
);
const OrderProcessRefund = lazyPage(
  () => import('./pages/app/orders/process-refund/[id]'),
);
const OrderSettingsUpsOAuth = lazyPage(
  () => import('./pages/app/orders/settings/ups/oauth'),
);
const OrderUpdateFulfillmentStatus = lazyPage(
  () => import('./pages/app/orders/update-fulfillment-status/[id]'),
);

// App - Redirect
const RedirectAmazon = lazyPage(() => import('./pages/app/redirect/amazon'));
const RedirectWalmart = lazyPage(() => import('./pages/app/redirect/walmart'));

// App - SCM
const ScmIndex = lazyPage(() => import('./pages/app/scm/index'));
const ScmAssignItems = lazyPage(() => import('./pages/app/scm/assign-items'));
const ScmRestock = lazyPage(() => import('./pages/app/scm/restock'));

// App - SCM - Purchase Orders
const PurchaseOrdersIndex = lazyPage(
  () => import('./pages/app/scm/purchase-orders/index'),
);
const PurchaseOrdersBulk = lazyPage(
  () => import('./pages/app/scm/purchase-orders/bulk'),
);
const PurchaseOrdersCreate = lazyPage(
  () => import('./pages/app/scm/purchase-orders/create'),
);
const PurchaseOrderDetails = lazyPage(
  () => import('./pages/app/scm/purchase-orders/[id]'),
);

const ShipmentsList = lazyPage(() => import('./pages/app/shipments/index'));

// App - SCM - Storage Provider
const StorageProviderIndex = lazyPage(
  () => import('./pages/app/scm/storage-provider/index'),
);
const StorageProviderDetails = lazyPage(
  () => import('./pages/app/scm/storage-provider/[id]'),
);
const StorageProviderInvoiceDetails = lazyPage(
  () => import('./pages/app/scm/storage-provider/invoice/[id]'),
);

// App - SCM - Supplier
const SupplierIndex = lazyPage(() => import('./pages/app/scm/supplier/index'));
const SupplierDetails = lazyPage(() => import('./pages/app/scm/supplier/[id]'));

// App - SCM - Work Orders
const WorkOrdersIndex = lazyPage(
  () => import('./pages/app/scm/work-orders/index'),
);
const WorkOrdersCreate = lazyPage(
  () => import('./pages/app/scm/work-orders/create'),
);
const WorkOrderDetails = lazyPage(
  () => import('./pages/app/scm/work-orders/[id]'),
);

// App - Wholesale
const WholesaleInvoiceDetails = lazyPage(
  () => import('./pages/app/wholesale/invoices/[id]'),
);
const WholesaleInvoiceCreate = lazyPage(
  () => import('./pages/app/wholesale/invoices/create'),
);
const WholesaleInvoiceListByStatus = lazyPage(
  () => import('./pages/app/wholesale/invoices/list/[status]'),
);
const WholesalersIndex = lazyPage(
  () => import('./pages/app/wholesale/wholesalers/index'),
);
const WholesalerDetails = lazyPage(
  () => import('./pages/app/wholesale/wholesalers/[id]'),
);

/* The sidebar's pages: their chunks load while the app is idle, so a click
   renders at once instead of waiting on a download. */
const SIDEBAR_PAGES = [
  DashboardAwaitingActions,
  CatalogIndex,
  OrderListByStatus,
  StorageProviderIndex,
];

function AppRoutes() {
  useEffect(() => {
    const preload = () => {
      for (const page of SIDEBAR_PAGES) page.preload();
    };
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(preload, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    }
    const timer = setTimeout(preload, 1000);
    return () => clearTimeout(timer);
  }, []);

  function ExternalRedirect({ url }: { url: string }) {
    useEffect(() => {
      window.location.href = url;
    }, [url]);
    return null;
  }

  return (
    <Suspense fallback={<LoaderBar />}>
      <Routes>
        <Route element={<App />}>
          {/* Root level routes */}
          <Route path="/login" element={<Login />} />
          {/* Self sign-up is closed: new customers go through sales. */}
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/request-password" element={<RequestPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/redirect" element={<Redirect />} />
          <Route path="/sso" element={<Sso />} />
          <Route path="/handoff" element={<Handoff />} />
          <Route path="/cms-login" element={<CmsLogin />} />
          <Route path="/tactical" element={<Tactical />} />
          <Route path="/usps" element={<Usps />} />
          <Route path="/shopify-redirect" element={<ShopifyRedirect />} />

          {/* Shopify */}
          <Route path="/shopify" element={<ShopifyIndex />} />
          <Route path="/shopify/billing" element={<ShopifyBilling />} />

          {/* Public pages */}
          <Route path="/public/order" element={<PublicOrder />} />
          <Route
            path="/public/supplier/purchase-orders"
            element={<PublicSupplierPurchaseOrders />}
          />
          <Route
            path="/public/wholesaler/wholesaler-invoices"
            element={<PublicWholesalerInvoices />}
          />

          {/* App - Main */}
          <Route path="/app" element={<AppIndex />} />
          <Route path="/app/change-logs" element={<AppChangeLogs />} />
          <Route path="/app/notifications" element={<AppNotifications />} />
          <Route path="/app/onboarding" element={<AppOnboarding />} />
          <Route path="/app/settings" element={<AppSettings />} />
          <Route path="/app/billing/invoices" element={<BillingInvoices />} />
          {/* The old ShipNode page is a Settings tab now (EN-3277). */}
          <Route
            path="/app/skupreme-shipnode"
            element={<Navigate to="/app/settings?tab=shipnode" replace />}
          />

          {/* App - Catalogs */}
          <Route path="/app/catalogs" element={<CatalogsIndex />} />
          <Route
            path="/app/catalogs/attributes"
            element={<CatalogsAttributes />}
          />
          <Route
            path="/app/catalogs/classification-guide"
            element={<CatalogsClassificationGuide />}
          />
          <Route
            path="/app/catalogs/duplicate-listings"
            element={<CatalogsDuplicateListings />}
          />
          <Route
            path="/app/catalogs/resolve-conflict"
            element={<CatalogsResolveConflict />}
          />
          <Route path="/app/catalogs/catalog" element={<CatalogIndex />} />
          <Route path="/app/catalogs/mapping" element={<CatalogsMapping />} />
          <Route
            path="/app/catalogs/catalog/:id"
            element={<CatalogDetails />}
          />
          <Route
            path="/app/catalogs/components"
            element={<CatalogsComponentsIndex />}
          />
          <Route
            path="/app/catalogs/components/:id"
            element={<CatalogsComponentDetails />}
          />
          <Route
            path="/app/catalogs/listings/matched-items"
            element={<CatalogsListingsMatched />}
          />
          <Route
            path="/app/catalogs/listings/unmatched-items"
            element={<CatalogsListingsUnmatched />}
          />
          <Route
            path="/app/catalogs/settings/tiktok"
            element={<CatalogsSettingsTiktok />}
          />

          {/* App - CRM */}
          <Route path="/app/crm" element={<CrmIndex />} />
          <Route path="/app/crm/:id" element={<CrmDetails />} />

          {/* App - Dashboard */}
          <Route
            path="/app/dashboard/awaiting-actions"
            element={<DashboardAwaitingActions />}
          />
          <Route path="/app/dashboard/logs" element={<DashboardLogs />} />

          {/* App - Data Studio */}
          <Route
            path="/app/data-studio/analytics"
            element={<DataStudioAnalytics />}
          />
          <Route
            path="/app/data-studio/fba-monitoring-chart"
            element={<DataStudioFbaMonitoringChart />}
          />
          <Route
            path="/app/data-studio/inventory-report"
            element={<DataStudioInventoryReport />}
          />
          <Route
            path="/app/data-studio/lost-revenue"
            element={<DataStudioLostRevenue />}
          />
          <Route
            path="/app/data-studio/manifest-reports"
            element={<DataStudioManifestReports />}
          />
          <Route
            path="/app/data-studio/time-series"
            element={<DataStudioTimeSeries />}
          />

          {/* App - Orders */}
          <Route path="/app/orders/:id" element={<OrderDetails />} />
          <Route path="/app/orders/clone/:id" element={<OrderClone />} />
          <Route
            path="/app/orders/list/:status"
            element={<OrderListByStatus />}
          />
          <Route
            path="/app/orders/list/returns"
            element={<OrderListReturns />}
          />
          <Route
            path="/app/orders/list/shipped"
            element={<OrderListShipped />}
          />
          <Route
            path="/app/orders/process-refund/:id"
            element={<OrderProcessRefund />}
          />
          <Route
            path="/app/orders/settings/ups/oauth"
            element={<OrderSettingsUpsOAuth />}
          />
          <Route
            path="/app/orders/update-fulfillment-status/:id"
            element={<OrderUpdateFulfillmentStatus />}
          />

          {/* App - Redirect */}
          <Route path="/app/redirect/amazon" element={<RedirectAmazon />} />
          <Route path="/app/redirect/walmart" element={<RedirectWalmart />} />

          {/* App - SCM */}
          <Route path="/app/scm" element={<ScmIndex />} />
          <Route path="/app/scm/assign-items" element={<ScmAssignItems />} />
          <Route path="/app/scm/restock" element={<ScmRestock />} />

          {/* App - SCM - Purchase Orders */}
          <Route
            path="/app/scm/purchase-orders"
            element={<PurchaseOrdersIndex />}
          />
          <Route
            path="/app/scm/purchase-orders/bulk"
            element={<PurchaseOrdersBulk />}
          />
          <Route
            path="/app/scm/purchase-orders/create"
            element={<PurchaseOrdersCreate />}
          />
          <Route
            path="/app/scm/purchase-orders/:id"
            element={<PurchaseOrderDetails />}
          />

          {/* App - Shipments (EN-2766: every shipment type, read-only) */}
          <Route path="/app/shipments" element={<ShipmentsList />} />

          {/* App - SCM - Storage Provider */}
          <Route
            path="/app/scm/storage-provider"
            element={<StorageProviderIndex />}
          />
          <Route
            path="/app/scm/storage-provider/:id"
            element={<StorageProviderDetails />}
          />
          <Route
            path="/app/scm/storage-provider/invoice/:id"
            element={<StorageProviderInvoiceDetails />}
          />

          {/* App - SCM - Supplier */}
          <Route path="/app/scm/supplier" element={<SupplierIndex />} />
          <Route path="/app/scm/supplier/:id" element={<SupplierDetails />} />

          {/* App - SCM - Work Orders */}
          <Route path="/app/scm/work-orders" element={<WorkOrdersIndex />} />
          <Route
            path="/app/scm/work-orders/create"
            element={<WorkOrdersCreate />}
          />
          <Route
            path="/app/scm/work-orders/:id"
            element={<WorkOrderDetails />}
          />

          {/* App - Wholesale */}
          <Route
            path="/app/wholesale/invoices/:id"
            element={<WholesaleInvoiceDetails />}
          />
          <Route
            path="/app/wholesale/invoices/create"
            element={<WholesaleInvoiceCreate />}
          />
          <Route
            path="/app/wholesale/invoices/list/:status"
            element={<WholesaleInvoiceListByStatus />}
          />
          <Route
            path="/app/wholesale/wholesalers"
            element={<WholesalersIndex />}
          />
          <Route
            path="/app/wholesale/wholesalers/:id"
            element={<WholesalerDetails />}
          />

          <Route
            path="/mds"
            element={
              <ExternalRedirect url="https://hub.skupreme.com/affiliate/MDS" />
            }
          />

          {/* Default route - redirect to app */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
